import * as React from 'react'
import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    InputAdornment,
    Stack,
    Switch,
    TextField,
    Typography
} from '@mui/material'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import FavoriteBorderRoundedIcon from '@mui/icons-material/FavoriteBorderRounded'
import FavoriteRoundedIcon from '@mui/icons-material/FavoriteRounded'
import FilterAltRoundedIcon from '@mui/icons-material/FilterAltRounded'
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded'
import HandymanRoundedIcon from '@mui/icons-material/HandymanRounded'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import useAutoTranslate from '../hooks/useAutoTranslate'
import { buildSavedAttackTemplatesUrl } from './attackTemplatePlatform'
import UnitIconFrame, { resolveUnitLevelBadge } from './UnitIconFrame'

const SETTINGS_KEY = 'towerBotSettings'
const TOWER_PICKER_FAVORITES_KEY = 'towerBotTroopToolPickerFavorites'
const GLOBALS = { attackCountThreshold: '3500', supportWaveAmount: '600', minimumLadders: '10000', minimumRams: '10000' }
const DEFAULT_TROOP = { id: '216', label: '216: Valkyrie ranger', itemType: 'troop', statLine: 'Troop', level: '10' }
const EMPTY_TEMPLATE = { templateId: '', name: '', ownerUsername: '', description: '', template: null }
const KINGDOMS = [
    { key: 'green', title: 'Green', prefix: 'Green', pluginKey: 'attackbarronsempire' },
    { key: 'ice', title: 'Ice', prefix: 'Ice', pluginKey: 'attackbarronsglacier' },
    { key: 'sand', title: 'Sand', prefix: 'Desert', pluginKey: 'attackbarronssands' },
    { key: 'fire', title: 'Fire', prefix: 'Fire', pluginKey: 'attackbarronsfire' }
]

const cloneValue = value => typeof structuredClone === 'function' ? structuredClone(value) : JSON.parse(JSON.stringify(value ?? {}))
const textValue = (value, fallback = '') => [undefined, null].includes(value) ? fallback : String(value)
const searchValue = value => String(value ?? '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
const levelValue = (statLine, fallback = '') => String(statLine ?? '').match(/Level\s+([0-9]+)/i)?.[1] || textValue(fallback)

const normalizeTroop = value => ({
    id: textValue(value?.id ?? value?.value, DEFAULT_TROOP.id),
    label: textValue(value?.label, DEFAULT_TROOP.label),
    itemType: value?.itemType === 'tool' ? 'tool' : 'troop',
    statLine: textValue(value?.statLine, 'Troop'),
    level: textValue(value?.level, levelValue(value?.statLine, DEFAULT_TROOP.level)),
    iconUrl: textValue(value?.iconUrl),
    assetKey: textValue(value?.assetKey)
})

const normalizeTemplate = value => ({
    templateId: [undefined, null, ''].includes(value?.templateId) ? '' : String(value.templateId),
    name: textValue(value?.name),
    ownerUsername: textValue(value?.ownerUsername),
    description: textValue(value?.description),
    template: value?.template && typeof value.template === 'object' ? cloneValue(value.template) : null
})

export const normalizeTowerBotPluginState = pluginState => {
    const source = pluginState?.[SETTINGS_KEY] && typeof pluginState[SETTINGS_KEY] === 'object' ? pluginState[SETTINGS_KEY] : {}
    const runtimeEnabled = pluginState?.state
    const runtimeUseVipCommanders = pluginState?.useVipCommanders
    const useCoin = Boolean(source.useCoin ?? pluginState?.useCoin)
    return {
        attackCountThreshold: textValue(source.attackCountThreshold, GLOBALS.attackCountThreshold),
        supportWaveAmount: textValue(source.supportWaveAmount, GLOBALS.supportWaveAmount),
        minimumLadders: textValue(source.minimumLadders, GLOBALS.minimumLadders),
        minimumRams: textValue(source.minimumRams, GLOBALS.minimumRams),
        autoRestockTools: source.autoRestockTools !== false,
        enabled: runtimeEnabled === undefined ? Boolean(source.enabled) : Boolean(runtimeEnabled),
        singleTarget: Boolean(source.singleTarget ?? pluginState?.singleTarget),
        targetCoords: textValue(source.targetCoords, textValue(pluginState?.targetCoords)),
        targetMaxLevel: textValue(source.targetMaxLevel, textValue(pluginState?.targetMaxLevel)),
        maximumDistance: textValue(source.maximumDistance ?? source.maxDistance, textValue(pluginState?.maxDistance)),
        useVipCommanders: runtimeUseVipCommanders === undefined ? Boolean(source.useVipCommanders) : Boolean(runtimeUseVipCommanders),
        useUserTemplate: Boolean(source.useUserTemplate),
        useTimeSkips: Boolean(source.useTimeSkips ?? pluginState?.useTimeSkips),
        useCoin,
        useFeathers: !useCoin && Boolean(source.useFeathers ?? pluginState?.useLeather),
        troop: normalizeTroop(source.troop ?? DEFAULT_TROOP),
        attackTemplate: normalizeTemplate(source.attackTemplate ?? EMPTY_TEMPLATE)
    }
}

export const buildTowerPluginState = (userPlugins, nextConfig) => {
    const nextPlugins = cloneValue(userPlugins ?? {})

    KINGDOMS.forEach(section => {
        const kingdom = nextConfig.kingdoms[section.key]
        const useCoin = Boolean(kingdom.useCoin)
        const useFeathers = !useCoin && Boolean(kingdom.useFeathers)
        nextPlugins[section.pluginKey] ??= {}
        nextPlugins[section.pluginKey][SETTINGS_KEY] = {
            attackCountThreshold: nextConfig.globals.attackCountThreshold,
            supportWaveAmount: nextConfig.globals.supportWaveAmount,
            minimumLadders: nextConfig.globals.minimumLadders,
            minimumRams: nextConfig.globals.minimumRams,
            autoRestockTools: nextConfig.globals.autoRestockTools !== false,
            enabled: Boolean(kingdom.enabled),
            singleTarget: Boolean(kingdom.singleTarget),
            targetCoords: textValue(kingdom.targetCoords).trim(),
            targetMaxLevel: textValue(kingdom.targetMaxLevel).trim(),
            maximumDistance: textValue(kingdom.maximumDistance).trim(),
            useVipCommanders: Boolean(kingdom.useVipCommanders),
            useUserTemplate: Boolean(kingdom.useUserTemplate),
            useTimeSkips: Boolean(kingdom.useTimeSkips),
            useCoin,
            useFeathers,
            troop: normalizeTroop(kingdom.troop),
            attackTemplate: normalizeTemplate(kingdom.attackTemplate)
        }
        nextPlugins[section.pluginKey].state = Boolean(kingdom.enabled)
        nextPlugins[section.pluginKey].singleTarget = Boolean(kingdom.singleTarget)
        nextPlugins[section.pluginKey].targetCoords = textValue(kingdom.targetCoords).trim()
        nextPlugins[section.pluginKey].targetMaxLevel = textValue(kingdom.targetMaxLevel).trim()
        nextPlugins[section.pluginKey].maxDistance = textValue(kingdom.maximumDistance).trim()
        nextPlugins[section.pluginKey].useVipCommanders = Boolean(kingdom.useVipCommanders)
        nextPlugins[section.pluginKey].useTimeSkips = Boolean(kingdom.useTimeSkips)
        nextPlugins[section.pluginKey].useCoin = useCoin
        nextPlugins[section.pluginKey].useLeather = useFeathers
    })

    return nextPlugins
}

const buildPickerEntries = catalog => {
    const nextEntries = []
    const seenKeys = new Set()
    const appendEntries = (entries, itemType) => {
        for (const item of Array.isArray(entries) ? entries : []) {
            const id = textValue(item?.value ?? item?.id)
            const key = `${itemType}:${id}`
            if (!id || seenKeys.has(key))
                continue

            seenKeys.add(key)
            nextEntries.push({
                key,
                id,
                label: textValue(item?.label, item?.value ?? item?.id),
                itemType,
                level: levelValue(item?.statLine, item?.level),
                statLine: textValue(item?.statLine, itemType === 'tool' ? 'Tool' : 'Troop'),
                iconUrl: textValue(item?.iconUrl),
                assetKey: textValue(item?.assetKey),
                searchText: textValue(item?.searchText, `${item?.label ?? ''} ${item?.value ?? item?.id} ${item?.statLine ?? ''}`)
            })
        }
    }

    appendEntries(catalog?.attackTroops, 'troop')
    appendEntries(catalog?.defenseTroops, 'troop')
    appendEntries(catalog?.attackTools, 'tool')
    appendEntries(catalog?.defenseTools, 'tool')
    return nextEntries
}

const loadTowerPickerFavorites = () => {
    if (typeof window === 'undefined')
        return []

    try {
        const parsedValue = JSON.parse(window.localStorage.getItem(TOWER_PICKER_FAVORITES_KEY) ?? '[]')
        return Array.isArray(parsedValue) ? parsedValue.map(value => String(value)).filter(Boolean) : []
    }
    catch {
        return []
    }
}

const saveTowerPickerFavorites = favoriteKeys => {
    if (typeof window === 'undefined')
        return

    try {
        window.localStorage.setItem(TOWER_PICKER_FAVORITES_KEY, JSON.stringify(favoriteKeys))
    }
    catch { }
}

const inputSx = {
    '& .MuiOutlinedInput-root': { borderRadius: '10px', backgroundColor: 'rgba(23, 34, 55, 0.72)' },
    '& .MuiInputBase-input': { fontSize: '1.01rem', fontWeight: 700, color: '#f5f7ff' }
}

function Helper({ children }) {
    return (
        <Stack direction="row" spacing={0.6} alignItems="center" sx={{ mt: 0.7 }}>
            <InfoOutlinedIcon sx={{ fontSize: 13, color: '#95a5c4' }} />
            <Typography sx={{ fontSize: '0.89rem', color: '#95a5c4', lineHeight: 1.35 }}>{children}</Typography>
        </Stack>
    )
}

function FieldTitle({ children }) {
    return <Typography sx={{ color: '#f5f7ff', fontSize: '1rem', fontWeight: 800, mb: 0.9 }}>{children}</Typography>
}

function ToggleField({ checked, onChange, label, helper }) {
    return (
        <Box sx={{ minHeight: 54 }}>
            <Stack direction="row" spacing={1.2} alignItems="center" sx={{ pt: 1.15 }}>
                <Switch checked={checked} onChange={event => onChange(event.target.checked)} sx={{ width: 48, height: 29, p: 0, '& .MuiSwitch-switchBase': { p: '2.5px', '&.Mui-checked': { transform: 'translateX(18px)', color: '#ffffff' }, '&.Mui-checked + .MuiSwitch-track': { backgroundColor: '#2f7cff', opacity: 1 } }, '& .MuiSwitch-thumb': { width: 24, height: 24, boxShadow: 'none' }, '& .MuiSwitch-track': { opacity: 1, borderRadius: '999px', backgroundColor: 'rgba(111, 123, 143, 0.9)' } }} />
                <Typography sx={{ color: '#f5f7ff', fontSize: '1rem', fontWeight: 800 }}>{label}</Typography>
            </Stack>
            <Helper>{helper}</Helper>
        </Box>
    )
}

function SectionHeading({ children }) {
    return (
        <Stack direction="row" alignItems="center" spacing={1.3} sx={{ mb: 2.2 }}>
            <Typography sx={{ color: '#f5f7ff', fontSize: '1.05rem', fontWeight: 800 }}>{children}</Typography>
            <Divider sx={{ flex: 1, borderColor: 'rgba(129, 146, 178, 0.22)' }} />
        </Stack>
    )
}

function PickerFilterButton({ icon, label, count, active, onClick }) {
    return (
        <Button
            variant="outlined"
            onClick={onClick}
            startIcon={icon}
            sx={{
                minHeight: 36,
                px: 1.35,
                borderRadius: '10px',
                borderColor: active ? 'rgba(112, 136, 174, 0.56)' : 'rgba(112, 136, 174, 0.34)',
                backgroundColor: active ? 'rgba(74, 91, 120, 0.72)' : 'rgba(42, 54, 78, 0.56)',
                color: '#eef4ff',
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '0.96rem',
                gap: 0.8,
                '&:hover': {
                    borderColor: 'rgba(112, 136, 174, 0.58)',
                    backgroundColor: active ? 'rgba(82, 98, 127, 0.78)' : 'rgba(50, 63, 88, 0.68)'
                }
            }}
        >
            <Box component="span">{label}</Box>
            <Box
                component="span"
                sx={{
                    px: 0.75,
                    minWidth: 34,
                    minHeight: 24,
                    borderRadius: '8px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(255,255,255,0.08)',
                    color: '#aebad3',
                    fontWeight: 800,
                    fontSize: '0.95rem'
                }}
            >
                {count}
            </Box>
        </Button>
    )
}

function SearchDialog({ open, title, placeholder, items, onClose, onSelect, renderSecondary }) {
    const [searchTerm, setSearchTerm] = React.useState('')
    React.useEffect(() => { if (open) setSearchTerm('') }, [open])
    const filteredItems = React.useMemo(() => {
        const query = searchValue(searchTerm)
        return query ? items.filter(item => searchValue(item.searchText ?? `${item.label} ${item.description ?? ''}`).includes(query)) : items
    }, [items, searchTerm])
    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth={false} PaperProps={{ sx: { width: 'min(calc(100vw - 40px), 720px)', maxWidth: '720px', borderRadius: '16px', background: 'linear-gradient(180deg, rgba(31,44,70,0.98) 0%, rgba(24,36,59,0.98) 100%)', border: '1px solid rgba(255,255,255,0.08)' } }} BackdropProps={{ sx: { backdropFilter: 'blur(10px)', backgroundColor: 'rgba(4, 10, 20, 0.62)' } }}>
            <DialogTitle sx={{ px: 2.5, pt: 2.2, pb: 1.3 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography sx={{ fontSize: '1.2rem', fontWeight: 800, color: '#f3f7ff' }}>{title}</Typography>
                    <IconButton onClick={onClose} sx={{ color: '#a9b4ca' }}><CloseRoundedIcon /></IconButton>
                </Stack>
            </DialogTitle>
            <DialogContent sx={{ px: 2.5, pb: 2.5 }}>
                <TextField fullWidth placeholder={placeholder} value={searchTerm} onChange={event => setSearchTerm(event.target.value)} sx={{ mb: 2, '& .MuiOutlinedInput-root': { minHeight: 44, borderRadius: '10px', backgroundColor: 'rgba(17, 26, 44, 0.92)' } }} InputProps={{ startAdornment: <InputAdornment position="start"><SearchRoundedIcon sx={{ color: '#a9b4ca' }} /></InputAdornment> }} />
                <Box sx={{ maxHeight: 'min(52vh, 520px)', overflowY: 'auto', pr: 0.5 }}>
                    <Stack spacing={1.3}>
                        {filteredItems.map(item => (
                            <Box key={item.key ?? item.id} onClick={() => onSelect(item)} sx={{ p: 1.7, borderRadius: '12px', border: '1px solid rgba(255,255,255,0.08)', backgroundColor: 'rgba(255,255,255,0.03)', cursor: 'pointer', '&:hover': { backgroundColor: 'rgba(255,255,255,0.05)', borderColor: 'rgba(94, 157, 255, 0.36)' } }}>
                                <Typography sx={{ fontSize: '1rem', fontWeight: 800, color: '#f2f6ff' }}>{item.label}</Typography>
                                {renderSecondary ? renderSecondary(item) : null}
                            </Box>
                        ))}
                    </Stack>
                </Box>
            </DialogContent>
        </Dialog>
    )
}

function TroopToolPickerDialog({ open, items, onClose, onSelect }) {
    const [query, setQuery] = React.useState('')
    const [filterValue, setFilterValue] = React.useState('all')
    const [favoritesOnly, setFavoritesOnly] = React.useState(false)
    const [favoriteKeys, setFavoriteKeys] = React.useState(() => loadTowerPickerFavorites())
    const favoriteKeySet = React.useMemo(() => new Set(favoriteKeys), [favoriteKeys])

    React.useEffect(() => {
        if (!open)
            return

        setQuery('')
        setFilterValue('all')
        setFavoritesOnly(false)
    }, [open])

    React.useEffect(() => {
        saveTowerPickerFavorites(favoriteKeys)
    }, [favoriteKeys])

    const counts = React.useMemo(() => ({
        all: items.length,
        troops: items.filter(entry => entry.itemType === 'troop').length,
        tools: items.filter(entry => entry.itemType === 'tool').length
    }), [items])

    const filteredItems = React.useMemo(() => {
        const normalizedQuery = searchValue(query)

        return items
            .filter(entry => {
                if (filterValue === 'troops' && entry.itemType !== 'troop')
                    return false
                if (filterValue === 'tools' && entry.itemType !== 'tool')
                    return false
                if (favoritesOnly && !favoriteKeySet.has(entry.key))
                    return false
                if (!normalizedQuery)
                    return true

                return searchValue(`${entry.label} ${entry.id} ${entry.searchText}`).includes(normalizedQuery)
            })
            .sort((left, right) => {
                const leftFavorite = favoriteKeySet.has(left.key)
                const rightFavorite = favoriteKeySet.has(right.key)
                if (leftFavorite !== rightFavorite)
                    return leftFavorite ? -1 : 1

                return String(left.label ?? '').localeCompare(String(right.label ?? ''))
            })
    }, [favoriteKeySet, favoritesOnly, filterValue, items, query])

    const toggleFavorite = itemKey => {
        setFavoriteKeys(currentKeys =>
            currentKeys.includes(itemKey) ?
                currentKeys.filter(currentKey => currentKey !== itemKey) :
                [...currentKeys, itemKey]
        )
    }

    const handleClearFilters = () => {
        setQuery('')
        setFilterValue('all')
        setFavoritesOnly(false)
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth={false}
            PaperProps={{
                sx: {
                    width: 'min(calc(100vw - 20px), 566px)',
                    maxWidth: '566px',
                    minHeight: '724px',
                    maxHeight: 'min(calc(100vh - 20px), 724px)',
                    borderRadius: '14px',
                    overflow: 'hidden',
                    background: 'linear-gradient(180deg, rgba(39,51,74,0.98) 0%, rgba(31,43,66,0.98) 100%)',
                    border: '1px solid rgba(122, 143, 180, 0.32)',
                    boxShadow: '0 26px 60px rgba(0,0,0,0.42)'
                }
            }}
            BackdropProps={{
                sx: {
                    backdropFilter: 'blur(10px)',
                    backgroundColor: 'rgba(3, 8, 18, 0.68)'
                }
            }}
        >
            <DialogTitle sx={{ px: 2.2, pt: 2.05, pb: 1.25 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <SearchRoundedIcon sx={{ fontSize: 28, color: '#f4f7ff' }} />
                        <Typography sx={{ fontSize: '1.16rem', fontWeight: 800, color: '#f5f7ff' }}>
                            Troop & Tool Picker
                        </Typography>
                    </Stack>
                    <IconButton onClick={onClose} sx={{ color: '#9aa7c2', width: 30, height: 30 }}>
                        <CloseRoundedIcon />
                    </IconButton>
                </Stack>
            </DialogTitle>

            <DialogContent sx={{ px: 2.2, pt: 0, pb: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <Stack direction="row" spacing={1.1} sx={{ mb: 1.5 }}>
                    <TextField
                        fullWidth
                        placeholder="Search by name or ID..."
                        value={query}
                        onChange={event => setQuery(event.target.value)}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                minHeight: 42,
                                borderRadius: '10px',
                                backgroundColor: 'rgba(28, 41, 65, 0.9)',
                                '& fieldset': {
                                    borderColor: 'rgba(57, 121, 248, 0.95)',
                                    borderWidth: '2px'
                                },
                                '&:hover fieldset': {
                                    borderColor: 'rgba(74, 134, 255, 0.96)'
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: 'rgba(74, 134, 255, 0.96)'
                                }
                            },
                            '& .MuiInputBase-input': {
                                color: '#edf3ff',
                                fontSize: '0.98rem',
                                fontWeight: 600
                            }
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchRoundedIcon sx={{ color: '#a8b4cd', fontSize: 24 }} />
                                </InputAdornment>
                            )
                        }}
                    />
                    <Button
                        variant="outlined"
                        startIcon={favoriteKeys.length > 0 || favoritesOnly ? <FavoriteRoundedIcon sx={{ color: favoritesOnly ? '#ff8ea0' : '#ffffff' }} /> : <FavoriteBorderRoundedIcon />}
                        onClick={() => setFavoritesOnly(current => !current)}
                        sx={{
                            minWidth: 122,
                            minHeight: 42,
                            borderRadius: '10px',
                            borderColor: favoritesOnly ? 'rgba(255, 113, 141, 0.72)' : 'rgba(112, 136, 174, 0.34)',
                            backgroundColor: favoritesOnly ? 'rgba(255, 113, 141, 0.14)' : 'rgba(42, 54, 78, 0.56)',
                            color: '#f3f6ff',
                            textTransform: 'none',
                            fontWeight: 700,
                            fontSize: '0.96rem',
                            '&:hover': {
                                borderColor: favoritesOnly ? 'rgba(255, 113, 141, 0.82)' : 'rgba(112, 136, 174, 0.5)',
                                backgroundColor: favoritesOnly ? 'rgba(255, 113, 141, 0.2)' : 'rgba(50, 63, 88, 0.7)'
                            }
                        }}
                    >
                        Favorites
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<CloseRoundedIcon />}
                        onClick={handleClearFilters}
                        sx={{
                            minWidth: 108,
                            minHeight: 42,
                            borderRadius: '10px',
                            backgroundColor: '#ff3345',
                            color: '#ffffff',
                            textTransform: 'none',
                            fontWeight: 800,
                            fontSize: '0.96rem',
                            boxShadow: 'none',
                            '&:hover': {
                                backgroundColor: '#ff4858',
                                boxShadow: 'none'
                            }
                        }}
                    >
                        Clear
                    </Button>
                </Stack>

                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.55 }}>
                    <Stack direction="row" spacing={0.85}>
                        <PickerFilterButton icon={<FilterAltRoundedIcon sx={{ fontSize: 19 }} />} label="All" count={counts.all} active={filterValue === 'all'} onClick={() => setFilterValue('all')} />
                        <PickerFilterButton icon={<GroupsRoundedIcon sx={{ fontSize: 19 }} />} label="Troops" count={counts.troops} active={filterValue === 'troops'} onClick={() => setFilterValue('troops')} />
                        <PickerFilterButton icon={<HandymanRoundedIcon sx={{ fontSize: 19 }} />} label="Tools" count={counts.tools} active={filterValue === 'tools'} onClick={() => setFilterValue('tools')} />
                    </Stack>
                    <Typography sx={{ color: '#b3bfd7', fontWeight: 700, fontSize: '0.98rem' }}>
                        {`${filteredItems.length} results`}
                    </Typography>
                </Stack>

                <Box
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        borderRadius: '12px',
                        border: '1px solid rgba(123, 147, 186, 0.28)',
                        backgroundColor: 'rgba(31, 43, 67, 0.92)',
                        overflowY: 'auto',
                        overflowX: 'hidden'
                    }}
                >
                    {filteredItems.map((item, index) => {
                        const isFavorite = favoriteKeySet.has(item.key)
                        return (
                            <Box
                                key={item.key}
                                role="button"
                                tabIndex={0}
                                onClick={() => {
                                    onSelect(item)
                                    onClose()
                                }}
                                onKeyDown={event => {
                                    if (event.key === 'Enter' || event.key === ' ') {
                                        event.preventDefault()
                                        onSelect(item)
                                        onClose()
                                    }
                                }}
                                sx={{
                                    px: 2.15,
                                    py: 1.6,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: 1.2,
                                    cursor: 'pointer',
                                    borderBottom: index < filteredItems.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none',
                                    '&:hover': {
                                        backgroundColor: 'rgba(255,255,255,0.03)'
                                    }
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0, flex: 1 }}>
                                    <UnitIconFrame
                                        iconUrl={item.iconUrl}
                                        itemType={item.itemType}
                                        level={item.itemType === 'troop' ? resolveUnitLevelBadge(item) : ''}
                                        size={52}
                                    />
                                    <Box sx={{ minWidth: 0 }}>
                                        <Stack direction="row" alignItems="center" spacing={0.9} sx={{ mb: 0.6 }}>
                                            <Typography sx={{ color: '#5ea3ff', fontWeight: 800, fontSize: '1.01rem' }}>
                                                {`#${index + 1}`}
                                            </Typography>
                                            <Box
                                                sx={{
                                                    px: 0.95,
                                                    minHeight: 28,
                                                    borderRadius: '10px',
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    border: '1px solid rgba(130, 147, 177, 0.45)',
                                                    color: '#d6deec',
                                                    fontWeight: 700,
                                                    fontSize: '0.94rem'
                                                }}
                                            >
                                                {item.itemType === 'tool' ? 'Tool' : 'Troop'}
                                            </Box>
                                        </Stack>

                                        <Typography sx={{ color: '#f5f7ff', fontWeight: 700, fontSize: '1.14rem', lineHeight: 1.18 }}>
                                            {item.label}
                                        </Typography>
                                    </Box>
                                </Box>

                                <IconButton
                                    onClick={event => {
                                        event.stopPropagation()
                                        toggleFavorite(item.key)
                                    }}
                                    sx={{ color: isFavorite ? '#ff6e82' : '#9aa6bf', width: 34, height: 34 }}
                                >
                                    {isFavorite ? <FavoriteRoundedIcon /> : <FavoriteBorderRoundedIcon />}
                                </IconButton>
                            </Box>
                        )
                    })}
                </Box>

                <Box
                    sx={{
                        mt: 1.45,
                        px: 2.2,
                        py: 1.45,
                        mx: -2.2,
                        borderTop: '1px solid rgba(255,255,255,0.08)'
                    }}
                >
                    <Typography sx={{ color: '#7f8aa3', fontSize: '0.94rem' }}>
                        Click to select • Favorite to bookmark • Type an ID manually in the input field
                    </Typography>
                </Box>
            </DialogContent>
        </Dialog>
    )
}

function SearchField({ label, helper, value, placeholder, onOpen, ownerUsername, afterInput }) {
    return (
        <Box>
            <FieldTitle>{label}</FieldTitle>
            <TextField fullWidth value={value} placeholder={placeholder} onClick={onOpen} InputProps={{ readOnly: true, endAdornment: <InputAdornment position="end"><IconButton onClick={onOpen} edge="end" sx={{ color: '#d9e4fb' }}><SearchRoundedIcon /></IconButton></InputAdornment> }} sx={inputSx} />
            {afterInput}
            {ownerUsername ? <Stack direction="row" spacing={0.7} alignItems="center" sx={{ mt: 1 }}><PersonOutlineRoundedIcon sx={{ fontSize: 13, color: '#95a5c4' }} /><Typography sx={{ color: '#95a5c4', fontSize: '0.9rem' }}>{ownerUsername}</Typography></Stack> : null}
            <Helper>{helper}</Helper>
        </Box>
    )
}

export default function TowerBotSettingsPanel({ appLanguage = 'en', userPlugins, onChange, templatePlatform = 'gge', bthMode = false }) {
    const [templatePickerTarget, setTemplatePickerTarget] = React.useState('')
    const [troopPickerTarget, setTroopPickerTarget] = React.useState('')
    const [templates, setTemplates] = React.useState([])
    const [catalog, setCatalog] = React.useState({ attackTroops: [], defenseTroops: [], attackTools: [], defenseTools: [] })
    const t = useAutoTranslate(appLanguage, [
        'Automatic tool restocking',
        'Produce ladders and rams to maintain the minimum stock. Turn off to keep attacking without restocking tools.',
        'Green',
        'Ice',
        'Desert',
        'Fire',
        'Saved by',
        'Unknown',
        'results',
        'Kingdom',
        'Enable Kingdom',
        'Enable tower attacks in',
        'Single Target Mode',
        'Attack only one fixed target in',
        'Target Coords (X:Y)',
        'Coordinates used when Single Target Mode is enabled for',
        'Target Max Level',
        'Optional stop level for the selected target in',
        'kingdom. Leave empty to keep hitting it.',
        'Maximum Distance (Tiles)',
        'Troop ID',
        'Attack Template',
        'Select troop for',
        'kingdom attacks',
        'Optional template for',
        "towers. Used only when 'Use User Template' is enabled.",
        'Hard attack distance limit for',
        'kingdom. TowerBot prioritizes the closest towers first and, after each rescan, newly found nearby towers move to the front.',
        'Use User Template',
        'Use template mode for',
        'kingdom. If template is missing/invalid, TowerBot falls back to auto setup.',
        'Use Time Skips',
        'Use available time skips after tower attacks in',
        'Use Coins',
        'Use coin horses for',
        'Use Feathers',
        'Use feathers for',
        'kingdom tower attacks',
        'Use VIP Commanders',
        'Use only VIP or daily commanders detected from the game data for',
        'tower attacks'
    ])

    const globalPluginKey = React.useMemo(() => KINGDOMS.find(section => userPlugins?.[section.pluginKey]?.[SETTINGS_KEY])?.pluginKey ?? KINGDOMS[0].pluginKey, [userPlugins])
    const config = React.useMemo(() => {
        const globalSource = normalizeTowerBotPluginState(userPlugins?.[globalPluginKey])
        return {
            globals: {
                attackCountThreshold: globalSource.attackCountThreshold,
                supportWaveAmount: globalSource.supportWaveAmount,
                minimumLadders: globalSource.minimumLadders,
                minimumRams: globalSource.minimumRams,
                autoRestockTools: globalSource.autoRestockTools
            },
            kingdoms: Object.fromEntries(KINGDOMS.map(section => [section.key, normalizeTowerBotPluginState(userPlugins?.[section.pluginKey])]))
        }
    }, [globalPluginKey, userPlugins])
    const troopAndToolEntries = React.useMemo(() => buildPickerEntries(catalog), [catalog])
    const troopAndToolEntriesByKey = React.useMemo(
        () => new Map(troopAndToolEntries.map(entry => [`${entry.itemType}:${entry.id}`, entry])),
        [troopAndToolEntries]
    )
    const displayedSections = React.useMemo(() => bthMode ? [{ ...KINGDOMS[0], title: 'BTH', prefix: 'BTH' }] : KINGDOMS, [bthMode])

    const commit = React.useCallback(nextConfig => {
        onChange(buildTowerPluginState(userPlugins, nextConfig))
    }, [onChange, userPlugins])

    const update = updater => {
        const nextConfig = cloneValue(config)
        updater(nextConfig)
        commit(nextConfig)
    }

    React.useEffect(() => {
        fetch(buildSavedAttackTemplatesUrl(templatePlatform), { headers: { Accept: 'application/json' } })
            .then(response => response.ok ? response.json() : { templates: [] })
            .then(payload => setTemplates([...(Array.isArray(payload?.templates) ? payload.templates : [])].sort((left, right) => Number(right?.updatedAt ?? 0) - Number(left?.updatedAt ?? 0) || String(left?.name ?? '').localeCompare(String(right?.name ?? '')))))
            .catch(() => setTemplates([]))
    }, [templatePlatform])

    React.useEffect(() => {
        fetch(`/attack-template-catalog.json?language=${encodeURIComponent(appLanguage || 'en')}`)
            .then(response => response.ok ? response.json() : { attackTroops: [], defenseTroops: [], attackTools: [], defenseTools: [] })
            .then(payload => setCatalog({
                attackTroops: Array.isArray(payload?.attackTroops) ? payload.attackTroops : [],
                defenseTroops: Array.isArray(payload?.defenseTroops) ? payload.defenseTroops : [],
                attackTools: Array.isArray(payload?.attackTools) ? payload.attackTools : [],
                defenseTools: Array.isArray(payload?.defenseTools) ? payload.defenseTools : []
            }))
            .catch(() => setCatalog({ attackTroops: [], defenseTroops: [], attackTools: [], defenseTools: [] }))
    }, [appLanguage])

    return (
        <Stack spacing={3}>
            {!bthMode ? <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: 'repeat(2, minmax(0, 1fr))' }, gap: 2.4 }}>
                <Box><FieldTitle>Attack Count Threshold</FieldTitle><TextField fullWidth value={config.globals.attackCountThreshold} onChange={event => update(next => { next.globals.attackCountThreshold = event.target.value })} sx={inputSx} /><Helper>Number of attacks before stopping (Daily Limit is 3500 after that it will get more expensive)</Helper></Box>
                <Box><FieldTitle>Support Wave Amount</FieldTitle><TextField fullWidth value={config.globals.supportWaveAmount} onChange={event => update(next => { next.globals.supportWaveAmount = event.target.value })} sx={inputSx} /><Helper>Amount of troops in support wave (Dont go to high if you have low level green Towers)</Helper></Box>
            </Box> : null}

            {!bthMode ? <Box>
                <SectionHeading>Tool Buy</SectionHeading>
                <ToggleField checked={config.globals.autoRestockTools} onChange={value => update(next => { next.globals.autoRestockTools = value })} label={t('Automatic tool restocking')} helper={t('Produce ladders and rams to maintain the minimum stock. Turn off to keep attacking without restocking tools.')} />
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: 'repeat(2, minmax(0, 1fr))' }, gap: 2.4 }}>
                    <Box><FieldTitle>Minimum Ladders</FieldTitle><TextField fullWidth value={config.globals.minimumLadders} onChange={event => update(next => { next.globals.minimumLadders = event.target.value })} sx={inputSx} /><Helper>Minimum Amount the bot will keep the ladders at. If you dont have many coins put it at 1000</Helper></Box>
                    <Box><FieldTitle>Minimum Rams</FieldTitle><TextField fullWidth value={config.globals.minimumRams} onChange={event => update(next => { next.globals.minimumRams = event.target.value })} sx={inputSx} /><Helper>Minimum Amount the bot will keep the rams at. If you dont have many coins put it at 10000</Helper></Box>
                </Box>
            </Box> : null}

            {displayedSections.map(section => {
                const kingdom = config.kingdoms[section.key]
                const displayedTroop =
                    troopAndToolEntriesByKey.get(`${kingdom.troop.itemType}:${kingdom.troop.id}`) ??
                    kingdom.troop
                return (
                    <Box key={section.key}>
                        <SectionHeading>{section.title}</SectionHeading>
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: 'repeat(2, minmax(0, 1fr))' }, gap: 2.8 }}>
                            <Stack spacing={2.2}>
                                {!bthMode ? <ToggleField checked={kingdom.enabled} onChange={value => update(next => { next.kingdoms[section.key].enabled = value })} label={`${t(section.prefix)}: ${t('Enable Kingdom')}`} helper={`${t('Enable tower attacks in')} ${t(section.prefix).toLowerCase()} ${t('Kingdom').toLowerCase()}`} /> : null}
                                <ToggleField checked={kingdom.singleTarget} onChange={value => update(next => { next.kingdoms[section.key].singleTarget = value })} label={`${t(section.prefix)}: ${t('Single Target Mode')}`} helper={`${t('Attack only one fixed target in')} ${t(section.prefix).toLowerCase()} ${t('Kingdom').toLowerCase()}`} />
                                <Box>
                                    <FieldTitle>{`${t(section.prefix)}: ${t('Target Coords (X:Y)')}`}</FieldTitle>
                                    <TextField
                                        fullWidth
                                        value={kingdom.targetCoords}
                                        placeholder={`Example: 809:969`}
                                        onChange={event => update(next => { next.kingdoms[section.key].targetCoords = event.target.value })}
                                        sx={inputSx}
                                    />
                                    <Helper>{`${t('Coordinates used when Single Target Mode is enabled for')} ${t(section.prefix).toLowerCase()} ${t('Kingdom').toLowerCase()}`}</Helper>
                                </Box>
                                {!bthMode ? <Box>
                                    <FieldTitle>{`${t(section.prefix)}: ${t('Target Max Level')}`}</FieldTitle>
                                    <TextField
                                        fullWidth
                                        value={kingdom.targetMaxLevel}
                                        placeholder="Optional"
                                        onChange={event => update(next => { next.kingdoms[section.key].targetMaxLevel = event.target.value })}
                                        sx={inputSx}
                                    />
                                    <Helper>{`${t('Optional stop level for the selected target in')} ${t(section.prefix).toLowerCase()} ${t('kingdom. Leave empty to keep hitting it.')}`}</Helper>
                                </Box> : null}
                                {!bthMode ? <Box>
                                    <FieldTitle>{`${t(section.prefix)}: ${t('Maximum Distance (Tiles)')}`}</FieldTitle>
                                    <TextField
                                        fullWidth
                                        value={kingdom.maximumDistance}
                                        placeholder="Optional"
                                        onChange={event => update(next => { next.kingdoms[section.key].maximumDistance = event.target.value })}
                                        sx={inputSx}
                                        inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                                    />
                                    <Helper>{`${t('Hard attack distance limit for')} ${t(section.prefix).toLowerCase()} ${t('kingdom. TowerBot prioritizes the closest towers first and, after each rescan, newly found nearby towers move to the front.')}`}</Helper>
                                </Box> : null}
                                {!bthMode ? <SearchField
                                    label={`${t(section.prefix)}: ${t('Troop ID')}`}
                                    helper={`${t('Select troop for')} ${t(section.prefix).toLowerCase()} ${t('kingdom attacks')}`}
                                    value={displayedTroop.label}
                                    placeholder={`${t('Select troop for')} ${t(section.prefix).toLowerCase()} ${t('kingdom attacks')}`}
                                    onOpen={() => setTroopPickerTarget(section.key)}
                                    afterInput={(
                                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                                            <UnitIconFrame
                                                iconUrl={displayedTroop.iconUrl}
                                                itemType={displayedTroop.itemType}
                                                level={displayedTroop.itemType === 'troop' ? resolveUnitLevelBadge(displayedTroop) : ''}
                                                size={36}
                                            />
                                            <Chip label={displayedTroop.itemType === 'tool' ? 'Tool' : 'Troop'} size="small" variant="outlined" sx={{ borderColor: 'rgba(255,255,255,0.12)', color: '#c3d2eb' }} />
                                            {displayedTroop.level ? <Chip label={`Level ${displayedTroop.level}`} size="small" variant="outlined" sx={{ borderColor: 'rgba(255,255,255,0.12)', color: '#8fa3c8' }} /> : null}
                                        </Stack>
                                    )}
                                /> : null}
                                <SearchField label={`${t(section.prefix)}: ${t('Attack Template')}`} helper={`${t('Optional template for')} ${t(section.prefix)} ${t("towers. Used only when 'Use User Template' is enabled.")}`} value={kingdom.attackTemplate.name} placeholder={`${t('Optional template for')} ${t(section.prefix)} ${t("towers. Used only when 'Use User Template' is enabled.")}`} ownerUsername={kingdom.attackTemplate.ownerUsername} onOpen={() => setTemplatePickerTarget(section.key)} />
                            </Stack>
                            <Stack spacing={2.2}>
                                {!bthMode ? <ToggleField checked={kingdom.useUserTemplate} onChange={value => update(next => { next.kingdoms[section.key].useUserTemplate = value })} label={`${t(section.prefix)}: ${t('Use User Template')}`} helper={`${t('Use template mode for')} ${t(section.prefix).toLowerCase()} ${t('kingdom. If template is missing/invalid, TowerBot falls back to auto setup.')}`} /> : null}
                                <ToggleField checked={kingdom.useTimeSkips} onChange={value => update(next => { next.kingdoms[section.key].useTimeSkips = value })} label={`${t(section.prefix)}: ${t('Use Time Skips')}`} helper={`${t('Use available time skips after tower attacks in')} ${t(section.prefix).toLowerCase()} ${t('kingdom tower attacks')}`} />
                                <ToggleField
                                    checked={kingdom.useCoin}
                                    onChange={value => update(next => {
                                        next.kingdoms[section.key].useCoin = value
                                        if (value)
                                            next.kingdoms[section.key].useFeathers = false
                                    })}
                                    label={`${t(section.prefix)}: ${t('Use Coins')}`}
                                    helper={`${t('Use coin horses for')} ${t(section.prefix).toLowerCase()} ${t('kingdom tower attacks')}`}
                                />
                                <ToggleField
                                    checked={kingdom.useFeathers}
                                    onChange={value => update(next => {
                                        next.kingdoms[section.key].useFeathers = value
                                        if (value)
                                            next.kingdoms[section.key].useCoin = false
                                    })}
                                    label={`${t(section.prefix)}: ${t('Use Feathers')}`}
                                    helper={`${t('Use feathers for')} ${t(section.prefix).toLowerCase()} ${t('kingdom tower attacks')}`}
                                />
                                {!bthMode ? <ToggleField checked={kingdom.useVipCommanders} onChange={value => update(next => { next.kingdoms[section.key].useVipCommanders = value })} label={`${t(section.prefix)}: ${t('Use VIP Commanders')}`} helper={`${t('Use only VIP or daily commanders detected from the game data for')} ${t(section.prefix).toLowerCase()} ${t('tower attacks')}`} /> : null}
                            </Stack>
                        </Box>
                    </Box>
                )
            })}

            <SearchDialog
                open={Boolean(templatePickerTarget)}
                title="Select Attack Template"
                placeholder="Search by name or user"
                items={templates.map(template => ({ ...template, key: `template:${template.id}`, label: template?.name || 'Untitled', searchText: `${template?.name ?? ''} ${template?.description ?? ''} ${template?.ownerUsername ?? ''}` }))}
                onClose={() => setTemplatePickerTarget('')}
                onSelect={template => {
                    update(next => {
                        next.kingdoms[templatePickerTarget].attackTemplate = normalizeTemplate({ templateId: String(template.id ?? ''), name: template.name, ownerUsername: template.ownerUsername, description: template.description, template: cloneValue(template.template ?? {}) })
                        if (bthMode)
                            next.kingdoms[templatePickerTarget].useUserTemplate = true
                    })
                    setTemplatePickerTarget('')
                }}
                renderSecondary={item => (
                    <>
                        <Typography sx={{ mt: 0.65, color: '#a8b4cc', fontSize: '0.94rem' }}>{item?.description || 'No description'}</Typography>
                        <Typography sx={{ mt: 1.05, color: '#94a4c3', fontSize: '0.9rem' }}>{`${t('Saved by')} ${item?.ownerUsername || t('Unknown')}`}</Typography>
                    </>
                )}
            />

            <TroopToolPickerDialog
                open={Boolean(troopPickerTarget)}
                items={troopAndToolEntries}
                onClose={() => setTroopPickerTarget('')}
                onSelect={entry => {
                    update(next => { next.kingdoms[troopPickerTarget].troop = normalizeTroop(entry) })
                    setTroopPickerTarget('')
                }}
            />
        </Stack>
    )
}
